/**
 * EngagementResultsPage — Phase 6a.12 (layout) per Spec §21.8.
 *
 * Phase 6a.11 introduced this page as a smart placeholder (4 metric
 * cards + info admonition). Phase 6a.12 promotes it to a *layout route*
 * that hosts the 6 results tabs via `<Outlet />`. Tab content lives in
 * child routes (URL-driven; `ResultsTabStrip` uses NavLink so refresh /
 * deep-link / browser-back all work without component-level state).
 *
 * Responsibilities retained from Phase 6a.11:
 *   • Load engagement from localStorage
 *   • Redirect on missing engagement or missing `lastResult`
 *   • Header bar (client, period, currency, status badge, calculatedAt)
 *   • Stale warning admonition
 *   • Recalculate button (transitionToStale + navigate)
 *
 * Responsibilities new in Phase 6a.12:
 *   • Compute `AuditMovementSchedule` once via `useMemo`
 *   • Render `<ResultsTabStrip>` below the header
 *   • Provide `{ engagement, schedule }` to children via Outlet context
 *
 * Removed in Phase 6a.12 (replaced by Balance Sheet Tab):
 *   • 4 metric-card panel
 *   • "Phase 6a.12'de eklenecektir" placeholder admonition
 */

import { Loader2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  PageContainer,
  PageHeader,
  ResultsLayout,
} from "@/components/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatIsoToDisplay } from "@/components/ui/date-picker.utils";
import {
  buildAuditMovementSchedule,
  type AuditMovementSchedule,
} from "@/features/engagement/audit-movement-schedule";
import { ResultsTabStrip } from "@/features/engagement/components/ResultsTabStrip";
import { StaleBanner } from "@/features/engagement/components/StaleBanner";
import { WarningsBanner } from "@/features/engagement/components/WarningsBanner";
import { transitionToStale } from "@/features/engagement/engagement-state";
import { useHistoryStorage } from "@/services";
import type { EngagementHistoryEntry } from "@/types";

function formatCalculatedAt(iso: string | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}

export interface EngagementResultsOutletContext {
  engagement: EngagementHistoryEntry;
  schedule: AuditMovementSchedule;
}

export function EngagementResultsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const historyStorage = useHistoryStorage();
  const [engagement, setEngagement] =
    React.useState<EngagementHistoryEntry | null>(null);
  const [loadState, setLoadState] = React.useState<
    "loading" | "loaded" | "missing"
  >("loading");

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!id) {
        if (!cancelled) setLoadState("missing");
        return;
      }
      const e = await historyStorage.getEngagement(id);
      if (cancelled) return;
      if (!e) {
        setLoadState("missing");
        return;
      }
      setEngagement(e);
      setLoadState("loaded");
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, historyStorage]);

  // Defensive redirects.
  React.useEffect(() => {
    if (loadState === "missing") {
      navigate("/history", { replace: true });
    } else if (loadState === "loaded" && engagement && !engagement.lastResult) {
      navigate(`/engagements/${engagement.id}/contracts`, { replace: true });
    }
  }, [loadState, engagement, navigate]);

  async function handleRecalculate() {
    if (!engagement) return;
    const updated = transitionToStale(engagement);
    await historyStorage.saveEngagement(updated);
    navigate(`/engagements/${engagement.id}/contracts`);
  }

  const [isExporting, setIsExporting] = React.useState(false);

  async function handleExportExcel() {
    if (!engagement?.lastResult || isExporting) return;
    setIsExporting(true);
    try {
      const { buildExcelWorkbook, generateFilename } = await import(
        "@/features/export/excelExport"
      );
      const { saveAs } = await import("file-saver");
      const buffer = await buildExcelWorkbook(engagement);
      saveAs(new Blob([buffer]), generateFilename(engagement));
      toast.success(t("export.excel.success"));
    } catch (err) {
      console.error("Excel export failed:", err);
      toast.error(t("export.excel.error"));
    } finally {
      setIsExporting(false);
    }
  }

  // Schedule built once per render via useMemo; downstream tabs read it
  // from `useOutletContext` without recomputing.
  const schedule = React.useMemo<AuditMovementSchedule | null>(() => {
    if (!engagement?.lastResult) return null;
    return buildAuditMovementSchedule({
      aggregations: engagement.lastResult.aggregations,
      // i18n's typed-keys union doesn't narrow when the utility passes
      // dynamic keys (e.g., "engagement.results.movement.types.*"). The
      // keys are all valid in the resource tree; widen at the boundary.
      t: t as unknown as (key: string) => string,
    });
  }, [engagement, t]);

  if (
    loadState !== "loaded" ||
    !engagement ||
    !engagement.lastResult ||
    !schedule
  ) {
    return (
      <PageContainer variant="reading">
        <p className="text-muted-foreground">{t("common.loading")}</p>
      </PageContainer>
    );
  }

  const status = engagement.status ?? "calculated";
  const statusLabel = t(
    `engagement.list.engagementStatus.${status}` as never,
  );
  const currency = engagement.functionalCurrency;

  // SPEC-08 §3.3 — slim sticky chrome row replaces the previous
  // <Card><CardHeader>. All previously-existing test-ids
  // (results-status-badge / export-excel-button / recalculate-button /
  // results-calculated-at) are carried onto the action elements
  // themselves.
  const headerPrimary = engagement.clientName;
  const headerMeta = (
    <>
      <span>
        {formatIsoToDisplay(engagement.auditPeriodStart)} —{" "}
        {formatIsoToDisplay(engagement.auditPeriodEnd)}
      </span>
      <span aria-hidden="true">·</span>
      <span className="font-medium text-foreground">{currency}</span>
      {engagement.lastResult?.calculatedAt && (
        <>
          <span aria-hidden="true">·</span>
          <span data-testid="results-calculated-at">
            {t("engagement.results.calculatedAt", {
              timestamp: formatCalculatedAt(
                engagement.lastResult.calculatedAt,
              ),
            })}
          </span>
        </>
      )}
    </>
  );
  const headerActions = (
    <>
      <Badge
        variant={status === "stale" ? "destructive" : "default"}
        data-testid="results-status-badge"
      >
        {statusLabel}
      </Badge>
      <Button
        type="button"
        variant="outline"
        onClick={() => void handleExportExcel()}
        disabled={!engagement.lastResult || isExporting}
        data-testid="export-excel-button"
      >
        {isExporting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t("export.excel.generating")}
          </>
        ) : (
          t("export.excel.button")
        )}
      </Button>
      <Button
        type="button"
        variant={status === "stale" ? "default" : "outline"}
        onClick={() => void handleRecalculate()}
        data-testid="recalculate-button"
      >
        {t("engagement.results.recalculate")}
      </Button>
    </>
  );

  return (
    <PageContainer variant="data" data-testid="engagement-results-page">
      <PageHeader
        primary={headerPrimary}
        meta={headerMeta}
        actions={headerActions}
      />

      {/* Phase 6a.18a — amber stale banner. Conditional on both
          `isStale` AND `lastResult`: a stale flag on a never-calculated
          engagement would be meaningless noise. The CTA reuses the
          existing recalculate handler so the auditor's mental model
          for "Hesapla" stays consistent across the page chrome. */}
      {engagement.isStale && engagement.lastResult && (
        <StaleBanner onRecalculate={() => void handleRecalculate()} />
      )}

      {/* Phase 6c.2 (Spec §21.15) — backend warnings banner. Sticky-amber,
          verbatim Big4-tone `messageTr`; session-only dismiss. */}
      <WarningsBanner
        warnings={engagement.lastResult.warnings ?? []}
        engagementId={engagement.id}
      />

      {/* Tab strip — URL-driven navigation. */}
      <div className="mb-4">
        <ResultsTabStrip engagementId={engagement.id} />
      </div>

      {/* SPEC-08 §3.5 — two-column shell. Right-rail content is added by
          individual tabs in their own SPECs; for this presentation pass
          the rail is rendered without content so the layout primitive is
          mounted but inert (no border / no reserved space).

          Active tab content rendered by react-router via the Outlet. */}
      <ResultsLayout rail={null}>
        <div className="space-y-6">
          <Outlet
            context={
              {
                engagement,
                schedule,
              } satisfies EngagementResultsOutletContext
            }
          />
        </div>
      </ResultsLayout>
    </PageContainer>
  );
}
